import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  Input,
  Label,
  Spinner,
  UncontrolledTooltip,
} from "reactstrap";
import {
  updateMaterialForOption,
  addLaminate,
  mappedColors,
  getLaminates,
  updateEdgeMaterial,
  createEdgeMaterial,
  deleteColor,
  materialOptions,
} from "./service";
import { CustomizationSpinnerContainer, Notification } from "../common/style";
import { useParams } from "react-router-dom";
import {
  ColorPicker,
  ColorIcon,
  ProjectButton,
  UploadButton,
} from "../project/style";
import { debounce } from "lodash";
import NoImage from "../assets/img/no_image.png";
import { Option } from "../product/style";
import Select from "react-select";
import ConfirmationModal from "./ConfirmationModal";
import { LAM_BRANDS, LAM_CATEGORIES, LAM_THICKNESSES } from "../constants";

const Colors = ({ loadRoom, currentRoom, access, isAdmin, subTab }) => {
  const [colors, setColors] = useState(null);
  const [count, setCount] = useState(null);
  const [edgeTapeMapping, setEdgeTapeMapping] = useState(null);
  const [edgeMaterials, setEdgeMaterials] = useState(null);
  const [boards, setBoards] = useState(null);
  const [hinges, setHinges] = useState(null);
  const [channels, setChannels] = useState(null);
  const [showAddHinge, setShowAddHinge] = useState(false);
  const [showAddBoard, setShowAddBoard] = useState(false);
  const [showAddChannel, setShowAddChannel] = useState(false);
  const [editingHinge, setEditingHinge] = useState(null);
  const [editingBoard, setEditingBoard] = useState(null);
  const [editingChannel, setEditingChannel] = useState(null);
  const [availableHinges, setAvailableHinges] = useState([]);
  const [availableBoards, setAvailableBoards] = useState([]);
  const [availableChannels, setAvailableChannels] = useState([]);
  const [selectedHingeOption, setSelectedHingeOption] = useState(null);
  const [selectedBoardOption, setSelectedBoardOption] = useState(null);
  const [selectedChannelOption, setSelectedChannelOption] = useState(null);
  const [boardType, setBoardType] = useState("carcass");
  const [searchedColors, setSearchedColors] = useState([]);
  const [showSearchColors, setShowSearchColors] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedColor, setSelectedColor] = useState(null);
  const [colorSearchTerm, setColorSearchTerm] = useState("");
  const [colorSearchValue, setColorSearchValue] = useState("");
  const [colorsLoading, setColorsLoading] = useState(false);
  const [showUploadLaminate, setShowUploadLaminate] = useState(false);
  const [showUploadEdgeTape, setShowUploadEdgeTape] = useState(false);
  const uploadFileInputRef = useRef(null);
  const [laminateType, setLaminateType] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [disableUpload, setDisableUpload] = useState(true);
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Map sub-tab values to internal tab names
  const getTabFromSubTab = (subTabValue) => {
    if (!subTabValue) return "laminates";
    if (subTabValue === "colors") return "laminates";
    if (subTabValue === "boards") return "board";
    if (subTabValue === "hinges") return "hinge";
    if (subTabValue === "drawers") return "channel";
    return "laminates";
  };
  const [selectedTab, setSelectedTab] = useState(() => getTabFromSubTab(subTab));
  const params = useParams();
  const projectId = params.projectId;
  const [edgeCode, setEdgeCode] = useState("");
  const [edgeName, setEdgeName] = useState("");
  const [edgeThickness, setEdgeThickness] = useState("");
  const [edgeBrand, setEdgeBrand] = useState("");
  const [edgeWidth, setEdgeWidth] = useState("");
  const [showRemoveColorConfirmation, setShowRemoveColorConfirmation] =
    useState(false);
  const [deleteOption, setDeleteOption] = useState("");
  const [formDetails, setFormDetails] = useState({
    brand: "",
    range: "",
    code: "",
    name: "",
    finish: "",
    thickness: "",
    category: "",
    grains: "no",
  });

  const toggleRemoveColorConfirmation = () =>
    setShowRemoveColorConfirmation(!showRemoveColorConfirmation);

  const onDismissNotification = () => {
    setIsNotificationVisible(false);
  };

  const loadMappedColors = () => {
    setIsLoading(true);

    return mappedColors({ projectId: projectId })
      .then((response) => {
        handleMappedColorsResponse(response);
        return response;
      })
      .catch((error) => {
        setIsNotificationVisible(true);
        setNotification({ message: error.error, type: "error" });
        throw error;
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    loadMappedColors();
  }, []);

  const handleLaminateChange = (option, laminate) => {
    setActionLoading(true);

    updateMaterialForOption({
      projectId: projectId,
      optionIdentifier: option,
      materialIdentifier: laminate,
      materialCategory: "color",
    })
      .then((response) => {
        clearInfo();
        setColors(response.colors);
        setCount(response.count);
        setEdgeTapeMapping(response.edge_tape_mappings);
        setEdgeMaterials(response.edge_materials);
        loadRoom(currentRoom.id);
      })
      .catch(({ error }) => {
        setIsNotificationVisible(true);
        setNotification({ message: error, type: "error" });
      })
      .finally(() => {
        setActionLoading(false);
      });
  };

  const handleSearch = (searchValue) => {
    setColorSearchValue(searchValue);
    debounceSearch(searchValue);
  };

  const debouncedSearch = (value) => setColorSearchTerm(value);

  const debounceSearch = useCallback(debounce(debouncedSearch, 500), []);

  useEffect(() => {
    if (colorSearchTerm && laminateType) {
      setColorsLoading(true);

      // Use materialOptions endpoint with appropriate category
      const category = laminateType === "internal" ? "internal_color" : "external_color";

      materialOptions({ projectId, category })
        .then((response) => {
          // Response is an array of { identifier, name, mapped, ... }
          let filtered = Array.isArray(response) ? response : [];

          if (colorSearchTerm) {
            const searchLower = colorSearchTerm.toLowerCase();
            // Remove spaces from search term for comparison
            const searchWithoutSpaces = searchLower.replace(/\s+/g, "");
            filtered = filtered.filter((option) => {
              const name = (option.name || "").toLowerCase();
              const identifier = (option.identifier || "").toLowerCase();
              const code = (option.code || "").toLowerCase();
              const brand = (option.brand || "").toLowerCase();
              
              // Remove spaces from each field for comparison
              const nameWithoutSpaces = name.replace(/\s+/g, "");
              const identifierWithoutSpaces = identifier.replace(/\s+/g, "");
              const codeWithoutSpaces = code.replace(/\s+/g, "");
              const brandWithoutSpaces = brand.replace(/\s+/g, "");
              
              // Check both with and without spaces for flexibility
              return name.includes(searchLower) ||
                     identifier.includes(searchLower) ||
                     code.includes(searchLower) ||
                     brand.includes(searchLower) ||
                     nameWithoutSpaces.includes(searchWithoutSpaces) ||
                     identifierWithoutSpaces.includes(searchWithoutSpaces) ||
                     codeWithoutSpaces.includes(searchWithoutSpaces) ||
                     brandWithoutSpaces.includes(searchWithoutSpaces);
            });
          }

          setSearchedColors(filtered);
        })
        .catch((error) => {
          setIsNotificationVisible(true);
          setNotification({ message: error.error, type: "error" });
        })
        .finally(() => setColorsLoading(false));
    } else setSearchedColors([]);
  }, [colorSearchTerm, laminateType, projectId]);

  const clearInfo = () => {
    setShowSearchColors(false);
    setColorSearchValue("");
    setColorSearchTerm("");
    setLaminateType(null);
    setActionType(null);
    setSelectedColor(null);
  };

  useEffect(() => {
    if (
      formDetails.brand &&
      formDetails.range.trim() &&
      formDetails.code.trim() &&
      formDetails.name.trim() &&
      formDetails.finish.trim() &&
      formDetails.thickness &&
      formDetails.category &&
      selectedFile
    )
      setDisableUpload(false);
    else setDisableUpload(true);
  }, [formDetails, selectedFile]);

  useEffect(() => {
    setFormDetails({
      brand: "",
      range: "",
      code: "",
      name: "",
      finish: "",
      thickness: "",
      category: "",
      grains: "no",
    });
    if (uploadFileInputRef && uploadFileInputRef.current) removeFile();
    setErrorMessage(null);
  }, [showUploadLaminate]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      const fileType = file.type;
      const validImageTypes = ["image/jpeg", "image/png"];

      if (!validImageTypes.includes(fileType)) {
        setErrorMessage("Please select a valid image file.");
        setSelectedFile(null);
        event.target.value = "";
      } else {
        setErrorMessage("");
        setSelectedFile(file);
      }
    }
  };

  const removeFile = () => {
    uploadFileInputRef.current.value = "";
    setSelectedFile(null);
  };

  const uploadLaminate = (details, file) => {
    setActionLoading(true);

    addLaminate({ details, file })
      .then(({ message }) => {
        setShowUploadLaminate(false);
        setIsNotificationVisible(true);
        setNotification({ message: message, type: "success" });
      })
      .catch(({ error }) => {
        setIsNotificationVisible(true);
        setNotification({ message: error, type: "error" });
      })
      .finally(() => setActionLoading(false));
  };

  const handleEdgeMaterialChange = ({ edgeCategory, materialIdentifier }) => {
    setIsLoading(true);
    updateEdgeMaterial({
      projectId: projectId,
      edgeThickness: edgeCategory.thickness,
      edgeLaminateIdentifier: edgeCategory.laminate.identifier,
      edgePanelThickness: edgeCategory.panel_thickness,
      edgeMaterialIdentifier: materialIdentifier,
    })
      .then((response) => {
        handleMappedColorsResponse(response);
      })
      .catch(({ error }) => {
        setNotification({ message: error, type: "error" });
        setIsNotificationVisible(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleMappedColorsResponse = (response) => {
    setColors(response.colors);
    setCount(response.count);
    setEdgeTapeMapping(response.edge_tape_mappings);
    setEdgeMaterials(response.edge_materials);
    setBoards(response.boards);
    setHinges(response.hinges);
    setChannels(response.channels);
  };

  const getEdgeMaterialOption = (materialIdentifier, index) => {
    if (materialIdentifier === null) {
      return null;
    }
    const material = edgeMaterials.find(
      (material) => material.identifier === materialIdentifier
    );
    if (!material) {
      return null;
    }
    return {
      name: material.name,
      label: material.name,
      value: material.identifier,
    };
  };

  const metaDataFooter = (data) => ({
    alignItems: "left",
    display: "flex",
    paddingLeft: "10px",
    fontSize: "14px",
    paddingBottom: "8px",
    borderBottomStyle: "solid",
    borderBottomColor: "#ddd",
    borderBottomWidth: "1px",
    cursor: "pointer",
    ":after": {
      content: `"${data.width}\\00a0mm/${data.thickness}\\00a0mm"`,
      borderRadius: 10,
      display: "flex",
      marginRight: 8,
      height: 10,
      width: 10,
      color: "#6c757d",
      fontSize: "12px",
    },
  });

  const colourStyles = {
    control: (styles) => ({ ...styles, backgroundColor: "white" }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      return {
        display: "flex",
        alignContent: "left",
        flexGrow: 1,
        flexDirection: "column",
        ...metaDataFooter(data),
      };
    },
  };

  const createNewEdgeTape = (brand, name, code, thickness, width) => {
    setActionLoading(true);
    createEdgeMaterial({
      brand: brand,
      name: name,
      code: code,
      thickness: thickness,
      width: width,
    })
      .then((response) => {
        setShowUploadEdgeTape(false);
        setEdgeMaterials([...edgeMaterials, response]);
      })
      .catch(({ error }) => {
        setIsNotificationVisible(true);
        setNotification({ message: error, type: "error" });
      })
      .finally(() => setActionLoading(false));
  };

  const thicknessOptions = [
    { name: "0.5 mm", label: "0.5 mm", value: "0.5" },
    { name: "0.8 mm", label: "0.8 mm", value: "0.8" },
    { name: "1.3 mm", label: "1.3 mm", value: "1.3" },
    { name: "1.5 mm", label: "1.5 mm", value: "1.5" },
    { name: "2 mm", label: "2 mm", value: "2" },
  ];

  const widthOptions = [
    { name: "22 mm", label: "22 mm", value: "22" },
    { name: "25 mm", label: "25 mm", value: "25" },
    { name: "30 mm", label: "30 mm", value: "30" },
    { name: "40 mm", label: "40 mm", value: "40" },
  ];

  const brandOptions = [
    { name: "E3", label: "E3", value: "E3" },
    { name: "RD", label: "RD", value: "RD" },
    { name: "Rehau", label: "Rehau", value: "Rehau" },
    { name: "SmartEdge", label: "SmartEdge", value: "SmartEdge" },
    { name: "Star", label: "Star", value: "Star" },
    { name: "MasterEdge", label: "MasterEdge", value: "MasterEdge" },
    { name: "CrownEdge", label: "CrownEdge", value: "CrownEdge" },
  ];

  const deleteColorEntry = (optionIdentifier) => {
    setIsLoading(true);

    deleteColor({ projectId, optionIdentifier })
      .then((response) => handleMappedColorsResponse(response))
      .catch((error) => {
        setIsNotificationVisible(true);
        setNotification({ message: error.error, type: "error" });
      })
      .finally(() => {
        toggleRemoveColorConfirmation();
        setIsLoading(false);
      });
  };

  const loadAvailableOptions = async (category) => {
    try {
      let apiCategory;

      if (category === "board") {
        // Map board type to the appropriate category
        if (boardType === "carcass") {
          apiCategory = "carcass_board";
        } else if (boardType === "shutter") {
          apiCategory = "shutter_board";
        } else if (boardType === "backsheet") {
          apiCategory = "backsheet_board";
        } else {
          apiCategory = "carcass_board"; // default
        }
      } else if (category === "hinge") {
        apiCategory = "hinge";
      } else if (category === "channel") {
        apiCategory = "channel";
      } else {
        // For internal_color, external_color, or any other category, use as-is
        apiCategory = category;
      }

      const materials = await materialOptions({ projectId, category: apiCategory });

      if (category === "hinge") {
        setAvailableHinges(materials);
      } else if (category === "board") {
        setAvailableBoards(materials);
      } else if (category === "channel") {
        setAvailableChannels(materials);
      }
    } catch (error) {
      console.error("Error loading available options:", error);
      setIsNotificationVisible(true);
      setNotification({
        message: error?.error || error?.message || "Failed to load options",
        type: "error"
      });
      if (category === "hinge") {
        setAvailableHinges([]);
      } else if (category === "board") {
        setAvailableBoards([]);
      } else if (category === "channel") {
        setAvailableChannels([]);
      }
    }
  };

  const handleMaterialChange = async (optionIdentifier, materialIdentifier, materialCategory) => {
    if (!materialIdentifier) return;

    setActionLoading(true);
    try {
      let apiCategory;

      if (materialCategory === "board") {
        // Map board type to the appropriate category
        if (boardType === "carcass") {
          apiCategory = "carcass_board";
        } else if (boardType === "shutter") {
          apiCategory = "shutter_board";
        } else if (boardType === "backsheet") {
          apiCategory = "backsheet_board";
        } else {
          apiCategory = "carcass_board"; // default
        }
      } else if (materialCategory === "hinge") {
        apiCategory = "hinge";
      } else if (materialCategory === "channel") {
        apiCategory = "channel";
      } else {
        // For internal_color, external_color, or any other category, use as-is
        apiCategory = materialCategory;
      }
      await updateMaterialForOption({
        projectId,
        optionIdentifier: optionIdentifier || apiCategory,
        materialIdentifier,
        materialCategory: apiCategory,
      });
      await loadMappedColors();
      loadRoom(currentRoom.id);
      setShowAddHinge(false);
      setShowAddBoard(false);
      setShowAddChannel(false);
      setEditingHinge(null);
      setEditingBoard(null);
      setEditingChannel(null);
      setSelectedHingeOption(null);
      setSelectedBoardOption(null);
      setSelectedChannelOption(null);
      setIsNotificationVisible(true);
      setNotification({
        message: optionIdentifier ? "Material updated successfully" : "Material added successfully",
        type: "success"
      });
    } catch (error) {
      setIsNotificationVisible(true);
      setNotification({ message: error.error, type: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  // Update selectedTab when subTab prop changes
  useEffect(() => {
    let mappedTab = "laminates";
    if (subTab) {
      if (subTab === "colors") mappedTab = "laminates";
      else if (subTab === "boards") mappedTab = "board";
      else if (subTab === "hinges") mappedTab = "hinge";
      else if (subTab === "drawers") mappedTab = "channel";
    }
    setSelectedTab(mappedTab);
  }, [subTab]);

  useEffect(() => {
    if (selectedTab === "hinge" && (showAddHinge || editingHinge)) {
      loadAvailableOptions("hinge");
    }
  }, [selectedTab, showAddHinge, editingHinge]);

  useEffect(() => {
    if (selectedTab === "hinge" && availableHinges.length === 0 && !showAddHinge && !editingHinge) {
      loadAvailableOptions("hinge");
    }
  }, [selectedTab]);

  useEffect(() => {
    if (selectedTab === "board" && (showAddBoard || editingBoard)) {
      loadAvailableOptions("board");
    }
  }, [selectedTab, showAddBoard, editingBoard]);

  useEffect(() => {
    if (selectedTab === "channel" && (showAddChannel || editingChannel)) {
      loadAvailableOptions("channel");
    }
  }, [selectedTab, showAddChannel, editingChannel]);

  const categories = LAM_CATEGORIES.map((e) => {
    if (e === "lam_1") return { label: "Less than ₹350", value: e };
    else if (e === "lam_4500_plus")
      return { label: "More than ₹4500", value: e };
    else {
      const range = e.split("_").slice(1);

      return { label: `₹${range[0]} to ₹${range[1]}`, value: e };
    }
  });

  const brands = LAM_BRANDS.map((e) => ({
    label: e,
    value: e,
  }));

  const thicknesses = LAM_THICKNESSES.map((e) => ({
    label: e,
    value: e,
  }));

  return (
    <>
      {notification.type && (
        <Notification
          color={notification.type === "error" ? "danger" : "success"}
          isOpen={isNotificationVisible}
          toggle={onDismissNotification}
        >
          {notification.message}
        </Notification>
      )}

      <div className={`fh overflow-scroll ${isLoading ? "blurred" : ""}`}>
        {isLoading ? (
          <CustomizationSpinnerContainer>
            <Spinner />
          </CustomizationSpinnerContainer>
        ) : (
          <>
            {actionLoading && (
              <CustomizationSpinnerContainer top="200px">
                <Spinner />
              </CustomizationSpinnerContainer>
            )}
            {access.show_upload_laminate && showUploadLaminate ? (
              <div
                className={`${actionLoading ? "blurred" : ""}`}
                style={{ marginBottom: "10rem" }}
              >
                <div className="d-flex column-gap-3 align-items-center px-3 py-2">
                  <i
                    onClick={() => setShowUploadLaminate(false)}
                    className="fa-solid fa-arrow-left fs-5 pointer text-secondary"
                  />
                  <p className="fw-bold fs-6 m-0">Upload Laminate</p>
                </div>
                <div>
                  <div className="px-3 py-2">
                    <p className="mb-1 fw-bold">Brand</p>
                    <Select
                      options={brands}
                      value={brands.find((e) => e.value == formDetails.brand)}
                      onChange={(e) =>
                        setFormDetails({ ...formDetails, brand: e.value })
                      }
                    />
                  </div>
                  <div className="px-3 py-2">
                    <p className="mb-1 fw-bold">Color code</p>
                    <Input
                      type="text"
                      className="py-2 rounded-1"
                      value={formDetails.code}
                      onChange={(e) =>
                        setFormDetails({
                          ...formDetails,
                          code: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="px-3 py-2">
                    <p className="mb-1 fw-bold">Color name</p>
                    <Input
                      type="text"
                      className="py-2 rounded-1"
                      value={formDetails.name}
                      onChange={(e) =>
                        setFormDetails({
                          ...formDetails,
                          name: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="px-3 py-2">
                    <p className="mb-1 fw-bold">Collection</p>
                    <Input
                      type="text"
                      className="py-2 rounded-1"
                      value={formDetails.range}
                      onChange={(e) =>
                        setFormDetails({
                          ...formDetails,
                          range: e.target.value,
                        })
                      }
                    />
                    <div className="mt-2 text-secondary">
                      Ex: Liner, Solids.
                    </div>
                  </div>
                  <div className="px-3 py-2">
                    <p className="mb-1 fw-bold">Finish</p>
                    <Input
                      type="text"
                      className="py-2 rounded-1"
                      value={formDetails.finish}
                      onChange={(e) =>
                        setFormDetails({
                          ...formDetails,
                          finish: e.target.value,
                        })
                      }
                    />
                    <div className="mt-2 text-secondary">
                      Ex: SF, HGL, VNR, etc.
                    </div>
                  </div>
                  <div className="px-3 py-2">
                    <p className="mb-1 fw-bold">Thickness</p>
                    <Select
                      options={thicknesses}
                      value={thicknesses.find(
                        (e) => e.value == formDetails.thickness
                      )}
                      onChange={(e) =>
                        setFormDetails({ ...formDetails, thickness: e.value })
                      }
                    />
                  </div>
                  <div className="px-3 py-2">
                    <p className="mb-1 fw-bold">MRP range</p>
                    <Select
                      options={categories}
                      value={categories.find(
                        (e) => e.value == formDetails.category
                      )}
                      onChange={(e) =>
                        setFormDetails({ ...formDetails, category: e.value })
                      }
                    />
                    <div className="mt-2 text-secondary">
                      If price incl. tax is ₹1400, select 1250 - 1500.
                    </div>
                  </div>
                  <div className="px-3 py-2">
                    <div className="mb-3">
                      <p className="mb-1 fw-bold">Image</p>
                      <div className="d-flex align-items-center">
                        <Label
                          className="border border-secondary rounded-2 m-0"
                          htmlFor="file-upload"
                          style={{ padding: "6px 14px" }}
                        >
                          Select File
                        </Label>
                        <Input
                          className="d-none"
                          id="file-upload"
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          innerRef={uploadFileInputRef}
                        />

                        {selectedFile && (
                          <div
                            className="d-flex align-items-center ms-2 column-gap-1"
                            style={{ fontSize: "14px" }}
                          >
                            <span>{selectedFile.name}</span>
                            <i
                              className="fa-regular fa-circle-xmark pointer danger-on-hover"
                              onClick={() => removeFile()}
                            />
                          </div>
                        )}

                        {errorMessage && (
                          <span className="ms-2 text-danger">
                            {errorMessage}
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="mb-1 fw-bold">Has grains or patterns?</p>
                      <div className="btn-group">
                        <Option
                          onClick={() =>
                            setFormDetails({ ...formDetails, grains: "no" })
                          }
                          className={`default-option ${
                            formDetails.grains === "no" && "selected-option"
                          }`}
                        >
                          No
                        </Option>
                        <Option
                          onClick={() =>
                            setFormDetails({ ...formDetails, grains: "yes" })
                          }
                          className={`default-option ${
                            formDetails.grains === "yes" && "selected-option"
                          }`}
                        >
                          Yes
                        </Option>
                      </div>
                      <div className="mt-2 text-secondary">
                        Note: If set to <b>No</b>, this laminate may be rotated
                        during fabrication.
                      </div>
                    </div>
                  </div>
                  <div className="d-flex px-3 py-2 column-gap-3">
                    <ProjectButton
                      onClick={() => uploadLaminate(formDetails, selectedFile)}
                      disabled={disableUpload || actionLoading}
                      className="rounded-1"
                    >
                      Upload
                    </ProjectButton>
                    <Button
                      onClick={() => setShowUploadLaminate(false)}
                      className="rounded-1"
                      outline
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            ) : showSearchColors ? (
              <>
                <div className="d-flex justify-content-between align-items-center border-bottom px-3 py-2">
                  <div className="d-flex column-gap-3 align-items-center">
                    <i
                      onClick={() => clearInfo()}
                      className="fa-solid fa-arrow-left fs-5 pointer"
                    />
                    {actionType === "add" ? (
                      <p className="fw-bold fs-6 m-0">Add Color</p>
                    ) : (
                      <div className="d-flex column-gap-2 align-items-center">
                        <ColorPicker className="m-0" height="40px" width="40px">
                          <img
                            className="h-100 w-100"
                            src={selectedColor.image}
                          />
                        </ColorPicker>
                        <p className="m-0">{selectedColor.short_name}</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className={`${actionLoading ? "blurred" : ""}`}>
                  <div className="p-3">
                    <Input
                      type="search"
                      value={colorSearchValue}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="rounded-1 px-3 py-2"
                      placeholder="Search by laminate code, name or brand"
                      autoFocus
                    />
                  </div>
                  {!colorSearchValue && (
                    <Card className="d-flex justify-content-center align-items-center empty px-3 m-3">
                      Type to search laminates
                    </Card>
                  )}
                  <div
                    className={`${colorsLoading ? "blurred" : ""} px-3`}
                    style={{ paddingBottom: "70px" }}
                  >
                    {colorsLoading ? (
                      <div className="mt-2 text-center">
                        <CustomizationSpinnerContainer>
                          <Spinner />
                        </CustomizationSpinnerContainer>
                      </div>
                    ) : (
                      <>
                        {searchedColors.length > 0 ? (
                          <div className="d-flex flex-wrap column-gap-4 row-gap-2">
                            {searchedColors.map((color, index) => {
                              const isDisabled =
                                (selectedColor &&
                                  selectedColor.rotatable &&
                                  !color.rotatable) ||
                                color.archived ||
                                color.mapped === true;

                              return (
                                <>
                                  <div
                                    id={"color_" + index}
                                    className={`d-flex flex-column align-items-center ${
                                      isDisabled
                                        ? "not-allowed-cursor opacity-25"
                                        : "pointer"
                                    }`}
                                    onClick={() => {
                                      if (isDisabled) return;

                                      handleLaminateChange(
                                        selectedColor
                                          ? selectedColor.prop_identifier
                                          : laminateType,
                                        color.identifier
                                      );
                                    }}
                                    key={index}
                                  >
                                    <ColorPicker
                                      className={`${
                                        access.change_color && "disabled"
                                      }`}
                                      style={{
                                        opacity: color.mapped === true ? 0.5 : 1,
                                        pointerEvents: color.mapped === true ? "none" : "auto",
                                      }}
                                    >
                                      {(color.archived || color.unused_color || color.mapped === true) && (
                                        <div className="position-absolute top-50 start-0 translate-middle-y w-100 d-flex flex-column align-items-center gap-1">
                                          {color.archived && (
                                            <div className="w-100 bg-danger text-white text-center opacity-75 py-1 rounded-0">
                                              Archived
                                            </div>
                                          )}
                                          {color.unused_color && (
                                            <div className="w-100 bg-dark text-white text-center opacity-50 py-1 rounded-0">
                                              Unused
                                            </div>
                                          )}
                                          {color.mapped === true && (
                                            <div className="w-100 bg-secondary text-white text-center opacity-75 py-1 rounded-0">
                                              Mapped
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      <img
                                        className="h-100 w-100"
                                        src={color.image ? color.image : NoImage}
                                      />
                                    </ColorPicker>
                                    <p className="m-0">{color.short_name || color.name}</p>
                                  </div>
                                  <UncontrolledTooltip
                                    target={"color_" + index}
                                    placement="bottom"
                                  >
                                    {color.mapped === true
                                      ? "Already mapped - cannot select"
                                      : selectedColor &&
                                        selectedColor.rotatable &&
                                        !color.rotatable
                                      ? "Cannot change color from no-grain to grain"
                                      : color.name || color.short_name}
                                  </UncontrolledTooltip>
                                </>
                              );
                            })}
                          </div>
                        ) : (
                          colorSearchTerm.length > 0 && (
                            <div className="mt-2 text-center fw-bold">
                              No Laminates found
                            </div>
                          )
                        )}
                      </>
                    )}
                  </div>
                  <div
                    className="position-fixed bottom-0 d-flex align-items-center px-2 py-3 border-top border-secondary-subtle"
                    style={{
                      background: "#dddddd",
                      right: 0,
                      width: "300px",
                    }}
                  >
                    <p className="mb-0 me-1">Could not find the laminate?</p>
                    <UploadButton onClick={() => setShowUploadLaminate(true)}>
                      <i className="fa-solid fa-circle-plus me-1"></i>
                      Upload Laminate
                    </UploadButton>
                  </div>
                </div>
              </>
            ) : showUploadEdgeTape ? (
              <>
                <div className={`${actionLoading ? "blurred" : ""}`}>
                  <div className="d-flex column-gap-3 align-items-center px-2 py-3">
                    <i
                      onClick={() => setShowUploadEdgeTape(false)}
                      className="fa-solid fa-arrow-left fs-5 pointer text-secondary"
                    />
                    <p className="fw-bold fs-6 m-0">Add Edge Tape</p>
                  </div>
                  <div>
                    <div className="px-3 py-2">
                      <p className="mb-1 fw-bold">Brand</p>
                      <Select
                        value={edgeBrand}
                        className="rounded-1 w-50"
                        options={brandOptions}
                        onChange={(e) => {
                          setEdgeBrand(e);
                        }}
                        placeholder={"Select brand"}
                      />
                    </div>
                    <div className="px-3 py-2">
                      <p className="mb-1 fw-bold">Code</p>
                      <Input
                        value={edgeCode}
                        onChange={(ev) => setEdgeCode(ev.target.value)}
                        type="text"
                        className="py-2 rounded-1 w-50"
                      />
                    </div>
                    <div className="px-3 py-2">
                      <p className="mb-1 fw-bold">Name</p>
                      <Input
                        value={edgeName}
                        onChange={(ev) => setEdgeName(ev.target.value)}
                        type="text"
                        className="py-2 rounded-1"
                      />
                    </div>
                    <div className="px-3 py-2">
                      <p className="mb-1 fw-bold">Width (mm)</p>
                      <Select
                        value={edgeWidth}
                        className="rounded-1 w-50"
                        options={widthOptions}
                        onChange={(e) => {
                          setEdgeWidth(e);
                        }}
                        placeholder={"Select width"}
                      />
                    </div>
                    <div className="px-3 py-2">
                      <p className="mb-1 fw-bold">Thickness</p>
                      <Select
                        value={edgeThickness}
                        className="rounded-1 w-50"
                        options={thicknessOptions}
                        onChange={(e) => {
                          setEdgeThickness(e);
                        }}
                        placeholder={"Select thickness"}
                      />
                    </div>

                    <div className="d-flex px-3 py-2 column-gap-3">
                      <ProjectButton
                        onClick={() =>
                          createNewEdgeTape(
                            edgeBrand.value,
                            edgeName,
                            edgeCode,
                            edgeThickness.value,
                            edgeWidth.value
                          )
                        }
                        className="rounded-1"
                        disabled={
                          !(edgeBrand && edgeName && edgeCode && edgeThickness)
                        }
                      >
                        Add Edge Tape
                      </ProjectButton>
                      <Button
                        onClick={() => setShowUploadEdgeTape(false)}
                        className="rounded-1"
                        outline
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="d-flex flex-row border-bottom ps-2 pt-2" style={{ minHeight: "40px", display: "flex !important", visibility: "visible !important", gap: "4px" }}>
                  <div
                    className={`tab align-self-center ${
                      selectedTab === "laminates" ? "active" : ""
                    }`}
                    onClick={() => setSelectedTab("laminates")}
                    style={{ cursor: "pointer", padding: "8px 12px", display: "block", visibility: "visible" }}
                  >
                    Colors
                  </div>
                  <div
                    className={`tab align-self-center ${
                      selectedTab === "board" ? "active" : ""
                    }`}
                    onClick={() => setSelectedTab("board")}
                    style={{ cursor: "pointer", padding: "8px 12px", display: "block", visibility: "visible" }}
                  >
                    Boards
                  </div>
                  <div
                    className={`tab align-self-center ${
                      selectedTab === "hinge" ? "active" : ""
                    }`}
                    onClick={() => setSelectedTab("hinge")}
                    style={{ cursor: "pointer", padding: "8px 12px", display: "block", visibility: "visible" }}
                  >
                    Hinges
                  </div>
                  <div
                    className={`tab align-self-center ${
                      selectedTab === "channel" ? "active" : ""
                    }`}
                    onClick={() => setSelectedTab("channel")}
                    style={{ cursor: "pointer", padding: "8px 12px", display: "block", visibility: "visible" }}
                  >
                    Drawers
                  </div>
                  {access.view_edges && (
                    <div
                      className={`tab align-self-center ${
                        selectedTab === "edges" ? "active" : ""
                      }`}
                      onClick={() => setSelectedTab("edges")}
                      style={{ cursor: "pointer", padding: "8px 12px", display: "block", visibility: "visible" }}
                    >
                      Edges
                    </div>
                  )}
                </div>

                {selectedTab === "laminates" && (
                  <>
                    <div className="p-3">
                      <p className="mb-2 fw-bold fs-14px">Internal</p>
                      <div className="d-flex flex-wrap column-gap-4 row-gap-2">
                        {colors &&
                          colors.internal.map((color, index) => (
                            <div
                              className={`d-flex flex-column align-items-center ${
                                color.mapped ? "not-allowed-cursor" : ""
                              }`}
                              key={index}
                            >
                              <>
                                <ColorPicker
                                  id={"internal_" + index}
                                  style={{
                                    opacity: color.mapped ? 0.5 : 1,
                                    pointerEvents: color.mapped ? "none" : "auto",
                                  }}
                                >
                                  {(color.archived || color.unused_color || color.mapped) && (
                                    <div className="position-absolute top-50 start-0 translate-middle-y w-100 d-flex flex-column align-items-center gap-1">
                                      {color.archived && (
                                        <div className="w-100 bg-danger text-white text-center opacity-75 rounded-0">Archived</div>
                                      )}
                                      {color.unused_color && (
                                        <div className="w-100 bg-dark text-white text-center opacity-50 rounded-0">Unused</div>
                                      )}
                                    </div>
                                  )}

                                  <img
                                    className="h-100 w-100"
                                    src={color.image}
                                  />
                                </ColorPicker>
                                <UncontrolledTooltip
                                  target={"internal_" + index}
                                  placement="bottom"
                                >
                                  {color.name}
                                </UncontrolledTooltip>
                              </>
                              <p className="m-0">{color.short_name}</p>
                              {access.change_color && !isAdmin && (
                                <div className="d-flex justify-content-between align-items-center">
                                  <>
                                    <i
                                      id={"internal_update_" + index}
                                      className="fa-regular fa-pen-to-square icon-hover pointer"
                                      onClick={() => {
                                        setShowSearchColors(true);
                                        setLaminateType("internal");
                                        setActionType("update");
                                        setSelectedColor(color);
                                      }}
                                    ></i>
                                    <UncontrolledTooltip
                                      target={"internal_update_" + index}
                                      placement="bottom"
                                    >
                                      Change Color
                                    </UncontrolledTooltip>
                                  </>
                                  <>
                                    <i
                                      id={"internal_delete_" + index}
                                      className={`fa-regular fa-trash-can icon-hover ${
                                        !color.unused_color
                                          ? "grey_letter not-allowed-cursor"
                                          : "pointer"
                                      }`}
                                      onClick={() => {
                                        if (color.unused_color) {
                                          setDeleteOption(
                                            color.prop_identifier
                                          );
                                          toggleRemoveColorConfirmation();
                                        }
                                      }}
                                    ></i>
                                    <UncontrolledTooltip
                                      target={"internal_delete_" + index}
                                      placement="bottom"
                                    >
                                      {color.unused_color
                                        ? "Delete Color"
                                        : "Color in use cannot be deleted"}
                                    </UncontrolledTooltip>
                                  </>
                                </div>
                              )}
                            </div>
                          ))}
                        {access.add_color && !isAdmin &&
                          colors &&
                          colors.internal.length <= count["internal"] && (
                            <div className="d-flex flex-column align-items-center">
                              <ColorPicker
                                className="d-flex justify-content-center align-items-center pointer"
                                style={{ background: "#D9D9D9" }}
                                onClick={() => {
                                  setShowSearchColors(true);
                                  setLaminateType("internal");
                                  setActionType("add");
                                }}
                              >
                                <i className="fa-solid fa-plus fs-5 text-secondary" />
                              </ColorPicker>
                              <span className="fs-10px text-secondary">
                                Add Color
                              </span>
                            </div>
                          )}
                      </div>
                    </div>
                    <div className="px-3">
                      <p className="mb-2 fw-bold fs-14px">External</p>
                      <div className="d-flex flex-wrap column-gap-4 row-gap-2">
                        {colors &&
                          colors.external.map((color, index) => (
                            <div
                              className={`d-flex flex-column align-items-center ${
                                color.mapped ? "not-allowed-cursor" : ""
                              }`}
                              key={index}
                            >
                              <>
                                <ColorPicker
                                  id={"external" + index}
                                  style={{
                                    opacity: color.mapped ? 0.5 : 1,
                                    pointerEvents: color.mapped ? "none" : "auto",
                                  }}
                                >
                                  {(color.archived || color.unused_color || color.mapped) && (
                                    <div className="position-absolute top-50 start-0 translate-middle-y w-100 d-flex flex-column align-items-center gap-1">
                                      {color.archived && (
                                        <div className="w-100 bg-danger text-white text-center opacity-75 rounded-0">Archived</div>
                                      )}
                                      {color.unused_color && (
                                        <div className="w-100 bg-dark text-white text-center opacity-50 rounded-0">Unused</div>
                                      )}
                                      {color.mapped && (
                                        <div className="w-100 bg-secondary text-white text-center opacity-75 rounded-0">Mapped</div>
                                      )}
                                    </div>
                                  )}

                                  <img
                                    className="h-100 w-100"
                                    src={color.image}
                                  />
                                </ColorPicker>
                                <UncontrolledTooltip
                                  target={"external" + index}
                                  placement="bottom"
                                >
                                  {color.name}
                                </UncontrolledTooltip>
                              </>
                              <p className="m-0">{color.short_name}</p>
                              {access.change_color && !isAdmin && (
                                <div className="d-flex justify-content-between align-items-center">
                                  <>
                                    <i
                                      id={"external_update_" + index}
                                      className="fa-regular fa-pen-to-square icon-hover pointer"
                                      onClick={() => {
                                        setShowSearchColors(true);
                                        setLaminateType("external");
                                        setActionType("update");
                                        setSelectedColor(color);
                                      }}
                                    ></i>
                                    <UncontrolledTooltip
                                      target={"external_update_" + index}
                                      placement="bottom"
                                    >
                                      Change Color
                                    </UncontrolledTooltip>
                                  </>
                                  <>
                                    <i
                                      id={"external_delete_" + index}
                                      className={`fa-regular fa-trash-can icon-hover ${
                                        !color.unused_color
                                          ? "grey_letter not-allowed-cursor"
                                          : "pointer"
                                      }`}
                                      onClick={() => {
                                        if (color.unused_color) {
                                          setDeleteOption(
                                            color.prop_identifier
                                          );
                                          toggleRemoveColorConfirmation();
                                        }
                                      }}
                                    ></i>
                                    <UncontrolledTooltip
                                      target={"external_delete_" + index}
                                      placement="bottom"
                                    >
                                      {color.unused_color
                                        ? "Delete Color"
                                        : "Color in use cannot be deleted"}
                                    </UncontrolledTooltip>
                                  </>
                                </div>
                              )}
                            </div>
                          ))}
                        {access.add_color && !isAdmin &&
                          colors &&
                          colors.external.length <= count["external"] && (
                            <div className="d-flex flex-column align-items-center">
                              <ColorPicker
                                className="d-flex justify-content-center align-items-center pointer"
                                style={{ background: "#D9D9D9" }}
                                onClick={() => {
                                  setShowSearchColors(true);
                                  setLaminateType("external");
                                  setActionType("add");
                                }}
                              >
                                <i className="fa-solid fa-plus fs-5 text-secondary" />
                              </ColorPicker>
                              <span className="fs-10px text-secondary">
                                Add Color
                              </span>
                            </div>
                          )}
                      </div>
                    </div>
                  </>
                )}
                {selectedTab === "board" && (
                  <>
                    <div className="p-3">
                      <p className="mb-2 fw-bold fs-14px">Carcass</p>
                      {boards && boards.carcass && boards.carcass.length > 0 && (
                        <>
                          <div className="d-flex flex-column gap-2 mb-3">
                            {[...boards.carcass].sort((a, b) => {
                              if (a.unused === b.unused) return 0;
                              return a.unused ? 1 : -1;
                            }).map((board, index) => (
                              editingBoard && editingBoard.prop_identifier === board.prop_identifier && boardType === "carcass" ? (
                                <div key={index} className="border p-3 rounded mb-2">
                                  <p className="fw-bold mb-2">Edit Board</p>
                                  <div className="d-flex gap-2 align-items-end">
                                    <div className="flex-grow-1">
                                      <Select
                                        value={selectedBoardOption}
                                        options={availableBoards.map((b) => ({
                                          label: b.name,
                                          value: b.identifier,
                                          isDisabled: b.mapped === true,
                                        }))}
                                        onChange={(option) => setSelectedBoardOption(option)}
                                        placeholder="Select a Board"
                                      />
                                    </div>
                                    <Button
                                      size="sm"
                                      onClick={() => {
                                        if (selectedBoardOption) {
                                          handleMaterialChange(
                                            editingBoard.prop_identifier,
                                            selectedBoardOption.value,
                                            "board"
                                          );
                                        }
                                      }}
                                      disabled={!selectedBoardOption || actionLoading}
                                    >
                                      Update
                                    </Button>
                                    <Button
                                      size="sm"
                                      outline
                                      onClick={() => {
                                        setEditingBoard(null);
                                        setSelectedBoardOption(null);
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                              <div
                                key={index}
                                className="d-flex justify-content-between align-items-center border-bottom pb-2"
                              >
                                <div className="d-flex align-items-center gap-2">
                                  <span>{board.name || board.identifier}</span>
                                  {board.unused && (
                                    <span className="badge bg-secondary text-white" style={{ fontSize: "10px" }}>unused</span>
                                  )}
                                </div>
                                {access.change_color && !isAdmin && (
                                  <div className="d-flex gap-2">
                                    <i
                                      className="fa-regular fa-pen-to-square icon-hover pointer"
                                      onClick={() => {
                                        setEditingBoard(board);
                                        setBoardType("carcass");
                                        loadAvailableOptions("board");
                                        // Set the selected option to current board
                                        const currentOption = availableBoards.find(
                                          (b) => b.identifier === board.identifier
                                        );
                                        if (currentOption) {
                                          setSelectedBoardOption({
                                            label: currentOption.name,
                                            value: currentOption.identifier,
                                          });
                                        }
                                      }}
                                    ></i>
                                    <i
                                      className={`fa-regular fa-trash-can icon-hover ${
                                        !board.unused
                                          ? "grey_letter not-allowed-cursor"
                                          : "pointer"
                                      }`}
                                      onClick={() => {
                                        if (board.unused) {
                                          setDeleteOption(board.prop_identifier);
                                          toggleRemoveColorConfirmation();
                                        }
                                      }}
                                    ></i>
                                  </div>
                                )}
                              </div>
                              )
                            ))}
                          </div>
                          {showAddBoard && !editingBoard && boardType === "carcass" ? (
                            <div className="border rounded mb-3 bg-light">
                              <div className="d-flex align-items-center mb-3 text-white" style={{ backgroundColor: "grey" }}>
                                <p className="fw-bold p-2 mb-0">Add Board</p>
                              </div>
                              <div className="d-flex flex-column gap-3 px-3">
                                <div>
                                  <Select
                                    value={selectedBoardOption}
                                    options={availableBoards.map((board) => ({
                                      label: board.name,
                                      value: board.identifier,
                                      isDisabled: board.mapped === true,
                                    }))}
                                    onChange={(option) => setSelectedBoardOption(option)}
                                    placeholder="Select a Board"
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                  />
                                </div>
                                <div className="d-flex gap-2 justify-content-end pb-3">
                                  <Button
                                    size="md"
                                    color="primary"
                                    onClick={() => {
                                      if (selectedBoardOption) {
                                        handleMaterialChange(
                                          "carcass_material",
                                          selectedBoardOption.value,
                                          "board"
                                        );
                                      }
                                    }}
                                    disabled={!selectedBoardOption || actionLoading}
                                  >
                                    Add Board
                                  </Button>
                                  <Button
                                    size="md"
                                    outline
                                    onClick={() => {
                                      setShowAddBoard(false);
                                      setSelectedBoardOption(null);
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            !editingBoard && access.change_color && !isAdmin && (
                              <div
                                className="link pointer py-1"
                                onClick={() => {
                                  setShowAddBoard(true);
                                  setBoardType("carcass");
                                  loadAvailableOptions("board");
                                }}
                                style={{ color: "#2C92E6" }}
                              >
                                <i className="fa-solid fa-plus me-1"></i> Add Board
                              </div>
                            )
                          )}
                        </>
                      )}
                      {(!boards || !boards.carcass || boards.carcass.length === 0) && !editingBoard && access.change_color && !isAdmin && (
                        showAddBoard && boardType === "carcass" ? (
                          <div className="border rounded mb-3 bg-light">
                            <div className="d-flex align-items-center mb-3 text-white" style={{ backgroundColor: "grey" }}>
                              <p className="fw-bold p-2 mb-0">Add Board</p>
                            </div>
                            <div className="d-flex flex-column gap-3 px-3">
                              <div>
                                <Select
                                  value={selectedBoardOption}
                                  options={availableBoards.map((board) => ({
                                    label: board.name,
                                    value: board.identifier,
                                    isDisabled: board.mapped === true,
                                  }))}
                                  onChange={(option) => setSelectedBoardOption(option)}
                                  placeholder="Select a Board"
                                  className="react-select-container"
                                  classNamePrefix="react-select"
                                />
                              </div>
                              <div className="d-flex gap-2 justify-content-end pb-3">
                                <Button
                                  size="md"
                                  color="primary"
                                  onClick={() => {
                                    if (selectedBoardOption) {
                                      handleMaterialChange(
                                        "carcass_material",
                                        selectedBoardOption.value,
                                        "board"
                                      );
                                    }
                                  }}
                                  disabled={!selectedBoardOption || actionLoading}
                                >
                                  Add Board
                                </Button>
                                <Button
                                  size="md"
                                  outline
                                  onClick={() => {
                                    setShowAddBoard(false);
                                    setSelectedBoardOption(null);
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div
                            className="link pointer py-1 mb-3"
                            onClick={() => {
                              setShowAddBoard(true);
                              setBoardType("carcass");
                              loadAvailableOptions("board");
                            }}
                            style={{ color: "#2C92E6" }}
                          >
                            <i className="fa-solid fa-plus me-1"></i> Add Board
                          </div>
                        )
                      )}
                      <p className="mb-2 fw-bold fs-14px mt-3">Shutter</p>
                      {boards && boards.shutter && boards.shutter.length > 0 && (
                        <>
                          <div className="d-flex flex-column gap-2 mb-3">
                            {[...boards.shutter].sort((a, b) => {
                              if (a.unused === b.unused) return 0;
                              return a.unused ? 1 : -1;
                            }).map((board, index) => (
                              editingBoard && editingBoard.prop_identifier === board.prop_identifier && boardType === "shutter" ? (
                                <div key={index} className="border p-3 rounded mb-2">
                                  <p className="fw-bold mb-2">Edit Board</p>
                                  <div className="d-flex gap-2 align-items-end">
                                    <div className="flex-grow-1">
                                      <Select
                                        value={selectedBoardOption}
                                        options={availableBoards.map((b) => ({
                                          label: b.name,
                                          value: b.identifier,
                                          isDisabled: b.mapped === true,
                                        }))}
                                        onChange={(option) => setSelectedBoardOption(option)}
                                        placeholder="Select a Board"
                                      />
                                    </div>
                                    <Button
                                      size="sm"
                                      onClick={() => {
                                        if (selectedBoardOption) {
                                          handleMaterialChange(
                                            editingBoard.prop_identifier,
                                            selectedBoardOption.value,
                                            "board"
                                          );
                                        }
                                      }}
                                      disabled={!selectedBoardOption || actionLoading}
                                    >
                                      Update
                                    </Button>
                                    <Button
                                      size="sm"
                                      outline
                                      onClick={() => {
                                        setEditingBoard(null);
                                        setSelectedBoardOption(null);
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                              <div
                                key={index}
                                className="d-flex justify-content-between align-items-center border-bottom pb-2"
                              >
                                <div className="d-flex align-items-center gap-2">
                                  <span>{board.name || board.identifier}</span>
                                  {board.unused && (
                                    <span className="badge bg-secondary text-white" style={{ fontSize: "10px" }}>unused</span>
                                  )}
                                </div>
                                {access.change_color && !isAdmin && (
                                  <div className="d-flex gap-2">
                                    <i
                                      className="fa-regular fa-pen-to-square icon-hover pointer"
                                      onClick={() => {
                                        setEditingBoard(board);
                                        setBoardType("shutter");
                                        loadAvailableOptions("board");
                                        // Set the selected option to current board
                                        const currentOption = availableBoards.find(
                                          (b) => b.identifier === board.identifier
                                        );
                                        if (currentOption) {
                                          setSelectedBoardOption({
                                            label: currentOption.name,
                                            value: currentOption.identifier,
                                          });
                                        }
                                      }}
                                    ></i>
                                    <i
                                      className={`fa-regular fa-trash-can icon-hover ${
                                        !board.unused
                                          ? "grey_letter not-allowed-cursor"
                                          : "pointer"
                                      }`}
                                      onClick={() => {
                                        if (board.unused) {
                                          setDeleteOption(board.prop_identifier);
                                          toggleRemoveColorConfirmation();
                                        }
                                      }}
                                    ></i>
                                  </div>
                                )}
                              </div>
                              )
                            ))}
                          </div>
                          {showAddBoard && !editingBoard && boardType === "shutter" ? (
                            <div className="border rounded mb-3 bg-light">
                              <div className="d-flex align-items-center mb-3 text-white" style={{ backgroundColor: "grey" }}>
                                <p className="fw-bold p-2 mb-0">Add Board</p>
                              </div>
                              <div className="d-flex flex-column gap-3 px-3">
                                <div>
                                  <Select
                                    value={selectedBoardOption}
                                    options={availableBoards.map((board) => ({
                                      label: board.name,
                                      value: board.identifier,
                                      isDisabled: board.mapped === true,
                                    }))}
                                    onChange={(option) => setSelectedBoardOption(option)}
                                    placeholder="Select a Board"
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                  />
                                </div>
                                <div className="d-flex gap-2 justify-content-end pb-3">
                                  <Button
                                    size="md"
                                    color="primary"
                                    onClick={() => {
                                      if (selectedBoardOption) {
                                        handleMaterialChange(
                                          "shutter_material",
                                          selectedBoardOption.value,
                                          "board"
                                        );
                                      }
                                    }}
                                    disabled={!selectedBoardOption || actionLoading}
                                  >
                                    Add Board
                                  </Button>
                                  <Button
                                    size="md"
                                    outline
                                    onClick={() => {
                                      setShowAddBoard(false);
                                      setSelectedBoardOption(null);
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            !editingBoard && access.change_color && !isAdmin && (
                              <div
                                className="link pointer py-1"
                                onClick={() => {
                                  setShowAddBoard(true);
                                  setBoardType("shutter");
                                  loadAvailableOptions("board");
                                }}
                                style={{ color: "#2C92E6" }}
                              >
                                <i className="fa-solid fa-plus me-1"></i> Add Board
                              </div>
                            )
                          )}
                        </>
                      )}
                      {(!boards || !boards.shutter || boards.shutter.length === 0) && !editingBoard && access.change_color && !isAdmin && (
                        showAddBoard && boardType === "shutter" ? (
                          <div className="border rounded mb-3 bg-light">
                            <div className="d-flex align-items-center mb-3 text-white" style={{ backgroundColor: "grey" }}>
                              <p className="fw-bold p-2 mb-0">Add Board</p>
                            </div>
                            <div className="d-flex flex-column gap-3 px-3">
                              <div>
                                <Select
                                  value={selectedBoardOption}
                                  options={availableBoards.map((board) => ({
                                    label: board.name,
                                    value: board.identifier,
                                    isDisabled: board.mapped === true,
                                  }))}
                                  onChange={(option) => setSelectedBoardOption(option)}
                                  placeholder="Select a Board"
                                  className="react-select-container"
                                  classNamePrefix="react-select"
                                />
                              </div>
                              <div className="d-flex gap-2 justify-content-end pb-3">
                                <Button
                                  size="md"
                                  color="primary"
                                  onClick={() => {
                                    if (selectedBoardOption) {
                                      handleMaterialChange(
                                        "shutter_material",
                                        selectedBoardOption.value,
                                        "board"
                                      );
                                    }
                                  }}
                                  disabled={!selectedBoardOption || actionLoading}
                                >
                                  Add Board
                                </Button>
                                <Button
                                  size="md"
                                  outline
                                  onClick={() => {
                                    setShowAddBoard(false);
                                    setSelectedBoardOption(null);
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div
                            className="link pointer py-1 mb-3"
                            onClick={() => {
                              setShowAddBoard(true);
                              setBoardType("shutter");
                              loadAvailableOptions("board");
                            }}
                            style={{ color: "#2C92E6" }}
                          >
                            <i className="fa-solid fa-plus me-1"></i> Add Board
                          </div>
                        )
                      )}
                      {isAdmin && (
                        <>
                          <p className="mb-2 fw-bold fs-14px mt-3">Backsheet</p>
                          {boards && boards.backsheet && boards.backsheet.length > 0 && (
                            <>
                              <div className="d-flex flex-column gap-2 mb-3">
                                {[...boards.backsheet].sort((a, b) => {
                                  if (a.unused === b.unused) return 0;
                                  return a.unused ? 1 : -1;
                                }).map((board, index) => (
                              editingBoard && editingBoard.prop_identifier === board.prop_identifier && boardType === "backsheet" ? (
                                <div key={index} className="border p-3 rounded mb-2">
                                  <p className="fw-bold mb-2">Edit Board</p>
                                  <div className="d-flex gap-2 align-items-end">
                                    <div className="flex-grow-1">
                                      <Select
                                        value={selectedBoardOption}
                                        options={availableBoards.map((b) => ({
                                          label: b.name,
                                          value: b.identifier,
                                          isDisabled: b.mapped === true,
                                        }))}
                                        onChange={(option) => setSelectedBoardOption(option)}
                                        placeholder="Select a Board"
                                      />
                                    </div>
                                    <Button
                                      size="sm"
                                      onClick={() => {
                                        if (selectedBoardOption) {
                                          handleMaterialChange(
                                            editingBoard.prop_identifier,
                                            selectedBoardOption.value,
                                            "board"
                                          );
                                        }
                                      }}
                                      disabled={!selectedBoardOption || actionLoading}
                                    >
                                      Update
                                    </Button>
                                    <Button
                                      size="sm"
                                      outline
                                      onClick={() => {
                                        setEditingBoard(null);
                                        setSelectedBoardOption(null);
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                              <div
                                key={index}
                                className="d-flex justify-content-between align-items-center border-bottom pb-2"
                              >
                                <div className="d-flex align-items-center gap-2">
                                  <span>{board.name || board.identifier}</span>
                                  {board.unused && (
                                    <span className="badge bg-secondary text-white" style={{ fontSize: "10px" }}>unused</span>
                                  )}
                                </div>
                                {access.change_color && !isAdmin && (
                                  <div className="d-flex gap-2">
                                    <i
                                      className="fa-regular fa-pen-to-square icon-hover pointer"
                                      onClick={() => {
                                        setEditingBoard(board);
                                        setBoardType("backsheet");
                                        loadAvailableOptions("board");
                                        // Set the selected option to current board
                                        const currentOption = availableBoards.find(
                                          (b) => b.identifier === board.identifier
                                        );
                                        if (currentOption) {
                                          setSelectedBoardOption({
                                            label: currentOption.name,
                                            value: currentOption.identifier,
                                          });
                                        }
                                      }}
                                    ></i>
                                    <i
                                      className={`fa-regular fa-trash-can icon-hover ${
                                        !board.unused
                                          ? "grey_letter not-allowed-cursor"
                                          : "pointer"
                                      }`}
                                      onClick={() => {
                                        if (board.unused) {
                                          setDeleteOption(board.prop_identifier);
                                          toggleRemoveColorConfirmation();
                                        }
                                      }}
                                    ></i>
                                  </div>
                                )}
                              </div>
                              )
                            ))}
                          </div>
                          {showAddBoard && !editingBoard && boardType === "backsheet" ? (
                            <div className="border rounded mb-3 bg-light">
                              <div className="d-flex align-items-center mb-3 text-white" style={{ backgroundColor: "grey" }}>
                                <p className="fw-bold p-2 mb-0">Add Board</p>
                              </div>
                              <div className="d-flex flex-column gap-3 px-3">
                                <div>
                                  <Select
                                    value={selectedBoardOption}
                                    options={availableBoards.map((board) => ({
                                      label: board.name,
                                      value: board.identifier,
                                      isDisabled: board.mapped === true,
                                    }))}
                                    onChange={(option) => setSelectedBoardOption(option)}
                                    placeholder="Select a Board"
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                  />
                                </div>
                                <div className="d-flex gap-2 justify-content-end pb-3">
                                  <Button
                                    size="md"
                                    color="primary"
                                    onClick={() => {
                                      if (selectedBoardOption) {
                                        handleMaterialChange(
                                          "backsheet_material",
                                          selectedBoardOption.value,
                                          "board"
                                        );
                                      }
                                    }}
                                    disabled={!selectedBoardOption || actionLoading}
                                  >
                                    Add Board
                                  </Button>
                                  <Button
                                    size="md"
                                    outline
                                    onClick={() => {
                                      setShowAddBoard(false);
                                      setSelectedBoardOption(null);
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            !editingBoard && access.change_color && !isAdmin && (
                              <div
                                className="link pointer py-1"
                                onClick={() => {
                                  setShowAddBoard(true);
                                  setBoardType("backsheet");
                                  loadAvailableOptions("board");
                                }}
                                style={{ color: "#2C92E6" }}
                              >
                                <i className="fa-solid fa-plus me-1"></i> Add Board
                              </div>
                            )
                          )}
                        </>
                      )}
                      {isAdmin && (!boards || !boards.backsheet || boards.backsheet.length === 0) && !editingBoard && (
                        showAddBoard && boardType === "backsheet" ? (
                          <div className="border rounded mb-3 bg-light">
                            <div className="d-flex align-items-center mb-3 text-white" style={{ backgroundColor: "grey" }}>
                              <p className="fw-bold p-2 mb-0">Add Board</p>
                            </div>
                            <div className="d-flex flex-column gap-3 px-3">
                              <div>
                                <Select
                                  value={selectedBoardOption}
                                  options={availableBoards.map((board) => ({
                                    label: board.name,
                                    value: board.identifier,
                                    isDisabled: board.mapped === true,
                                  }))}
                                  onChange={(option) => setSelectedBoardOption(option)}
                                  placeholder="Select a Board"
                                  className="react-select-container"
                                  classNamePrefix="react-select"
                                />
                              </div>
                              <div className="d-flex gap-2 justify-content-end pb-3">
                                <Button
                                  size="md"
                                  color="primary"
                                  onClick={() => {
                                    if (selectedBoardOption) {
                                      handleMaterialChange(
                                        "backsheet_material",
                                        selectedBoardOption.value,
                                        "board"
                                      );
                                    }
                                  }}
                                  disabled={!selectedBoardOption || actionLoading}
                                >
                                  Add Board
                                </Button>
                                <Button
                                  size="md"
                                  outline
                                  onClick={() => {
                                    setShowAddBoard(false);
                                    setSelectedBoardOption(null);
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          </div>
                        ) : null
                      )}
                        </>
                      )}
                    </div>
                  </>
                )}
                {selectedTab === "hinge" && (
                  <>
                    <div className="p-3">
                      {hinges && hinges.length > 0 && (
                        <div className="d-flex flex-column gap-2 mb-3">
                          {[...hinges].sort((a, b) => {
                            if (a.unused === b.unused) return 0;
                            return a.unused ? 1 : -1;
                          }).map((hinge, index) => (
                            editingHinge && editingHinge.prop_identifier === hinge.prop_identifier ? (
                              <div key={index} className="border p-3 rounded mb-2">
                                <p className="fw-bold mb-2">Edit Hinge</p>
                                <div className="d-flex gap-2 align-items-end">
                                  <div className="flex-grow-1">
                                    <Select
                                      value={selectedHingeOption}
                                      options={availableHinges.map((h) => ({
                                        label: h.name,
                                        value: h.identifier,
                                        isDisabled: h.mapped === true,
                                      }))}
                                      onChange={(option) => setSelectedHingeOption(option)}
                                      placeholder="Select a Hinge"
                                    />
                                  </div>
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      if (selectedHingeOption) {
                                        handleMaterialChange(
                                          editingHinge.prop_identifier,
                                          selectedHingeOption.value,
                                          "hinge"
                                        );
                                      }
                                    }}
                                    disabled={!selectedHingeOption || actionLoading}
                                  >
                                    Update
                                  </Button>
                                  <Button
                                    size="sm"
                                    outline
                                    onClick={() => {
                                      setEditingHinge(null);
                                      setSelectedHingeOption(null);
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div
                                key={index}
                                className="d-flex justify-content-between align-items-center border-bottom pb-2"
                              >
                                <div className="d-flex align-items-center gap-2">
                                  <span>{hinge.name || hinge.identifier}</span>
                                  {hinge.unused && (
                                    <span className="badge bg-secondary text-white" style={{ fontSize: "10px" }}>unused</span>
                                  )}
                                </div>
                                {access.change_color && !isAdmin && (
                                  <div className="d-flex gap-2">
                                    <i
                                      className="fa-regular fa-pen-to-square icon-hover pointer"
                                      onClick={() => {
                                        setEditingHinge(hinge);
                                        loadAvailableOptions("hinge");
                                        // Set the selected option to current hinge
                                        const currentOption = availableHinges.find(
                                          (h) => h.identifier === hinge.identifier
                                        );
                                        if (currentOption) {
                                          setSelectedHingeOption({
                                            label: currentOption.name,
                                            value: currentOption.identifier,
                                          });
                                        }
                                      }}
                                    ></i>
                                    <i
                                      className={`fa-regular fa-trash-can icon-hover ${
                                        !hinge.unused
                                          ? "grey_letter not-allowed-cursor"
                                          : "pointer"
                                      }`}
                                      onClick={() => {
                                        if (hinge.unused) {
                                          setDeleteOption(hinge.prop_identifier);
                                          toggleRemoveColorConfirmation();
                                        }
                                      }}
                                    ></i>
                                  </div>
                                )}
                              </div>
                            )
                          ))}
                        </div>
                      )}
                      {showAddHinge && !editingHinge ? (
                        <div className="border rounded mb-3 bg-light">
                          <div className="d-flex align-items-center mb-3 text-white" style={{ backgroundColor: "grey" }}>
                            <p className="fw-bold p-2 mb-0">Add Hinge</p>
                          </div>
                          <div className="d-flex flex-column gap-3 px-3">
                            <div>
                              <Select
                                value={selectedHingeOption}
                                options={availableHinges.map((hinge) => ({
                                  label: hinge.name,
                                  value: hinge.identifier,
                                  isDisabled: hinge.mapped === true,
                                }))}
                                onChange={(option) => setSelectedHingeOption(option)}
                                placeholder="Choose a hinge from the list..."
                                className="react-select-container"
                                classNamePrefix="react-select"
                              />
                            </div>
                            <div className="d-flex gap-2 justify-content-end pb-3">
                              <Button
                                size="md"
                                color="primary"
                                onClick={() => {
                                  if (selectedHingeOption) {
                                    handleMaterialChange(
                                      null,
                                      selectedHingeOption.value,
                                      "hinge"
                                    );
                                  }
                                }}
                                disabled={!selectedHingeOption || actionLoading}
                              >
                                Add Hinge
                              </Button>
                              <Button
                                size="md"
                                outline
                                onClick={() => {
                                  setShowAddHinge(false);
                                  setEditingHinge(null);
                                  setSelectedHingeOption(null);
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        access.change_color && !isAdmin && (
                          <div
                            className="link pointer py-1"
                            onClick={() => {
                              setShowAddHinge(true);
                              loadAvailableOptions("hinge");
                            }}
                            style={{ color: "#2C92E6" }}
                          >
                            <i className="fa-solid fa-plus me-2"></i>Add Hinge
                          </div>
                        )
                      )}
                    </div>
                  </>
                )}
                {selectedTab === "channel" && (
                  <>
                    <div className="p-3">
                      {channels && channels.length > 0 && (
                        <div className="d-flex flex-column gap-2 mb-3">
                          {[...channels].sort((a, b) => {
                            if (a.unused === b.unused) return 0;
                            return a.unused ? 1 : -1;
                          }).map((channel, index) => (
                            editingChannel && editingChannel.prop_identifier === channel.prop_identifier ? (
                              <div key={index} className="border p-3 rounded mb-2">
                                <p className="fw-bold mb-2">Edit Drawer</p>
                                <div className="d-flex gap-2 align-items-end">
                                  <div className="flex-grow-1">
                                    <Select
                                      value={selectedChannelOption}
                                      options={availableChannels.map((ch) => ({
                                        label: ch.name,
                                        value: ch.identifier,
                                        isDisabled: ch.mapped === true,
                                      }))}
                                      onChange={(option) => setSelectedChannelOption(option)}
                                      placeholder="Select a Drawer"
                                    />
                                  </div>
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      if (selectedChannelOption) {
                                        handleMaterialChange(
                                          editingChannel.prop_identifier,
                                          selectedChannelOption.value,
                                          "channel"
                                        );
                                      }
                                    }}
                                    disabled={!selectedChannelOption || actionLoading}
                                  >
                                    Update
                                  </Button>
                                  <Button
                                    size="sm"
                                    outline
                                    onClick={() => {
                                      setEditingChannel(null);
                                      setSelectedChannelOption(null);
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div
                                key={index}
                                className="d-flex justify-content-between align-items-center border-bottom pb-2"
                              >
                                <div className="d-flex align-items-center gap-2">
                                  <span>{channel.name || channel.identifier}</span>
                                  {channel.unused && (
                                    <span className="badge bg-secondary text-white" style={{ fontSize: "10px" }}>unused</span>
                                  )}
                                </div>
                                {access.change_color && !isAdmin && (
                                  <div className="d-flex gap-2">
                                    <i
                                      className="fa-regular fa-pen-to-square icon-hover pointer"
                                      onClick={() => {
                                        setEditingChannel(channel);
                                        loadAvailableOptions("channel");
                                        // Set the selected option to current channel
                                        const currentOption = availableChannels.find(
                                          (ch) => ch.identifier === channel.identifier
                                        );
                                        if (currentOption) {
                                          setSelectedChannelOption({
                                            label: currentOption.name,
                                            value: currentOption.identifier,
                                          });
                                        }
                                      }}
                                    ></i>
                                    <i
                                      className={`fa-regular fa-trash-can icon-hover ${
                                        !channel.unused
                                          ? "grey_letter not-allowed-cursor"
                                          : "pointer"
                                      }`}
                                      onClick={() => {
                                        if (channel.unused) {
                                          setDeleteOption(channel.prop_identifier);
                                          toggleRemoveColorConfirmation();
                                        }
                                      }}
                                    ></i>
                                  </div>
                                )}
                              </div>
                            )
                          ))}
                        </div>
                      )}
                      {showAddChannel && !editingChannel ? (
                        <div className="border rounded mb-3 bg-light">
                          <div className="d-flex align-items-center mb-3 text-white" style={{ backgroundColor: "grey" }}>
                            <p className="fw-bold p-2 mb-0">Add Drawer</p>
                          </div>
                          <div className="d-flex flex-column gap-3 px-3">
                            <div>
                              <Select
                                value={selectedChannelOption}
                                options={availableChannels.map((channel) => ({
                                  label: channel.name,
                                  value: channel.identifier,
                                  isDisabled: channel.mapped === true,
                                }))}
                                onChange={(option) => setSelectedChannelOption(option)}
                                placeholder="Select a Drawer"
                                className="react-select-container"
                                classNamePrefix="react-select"
                              />
                            </div>
                            <div className="d-flex gap-2 justify-content-end pb-3">
                              <Button
                                size="md"
                                color="primary"
                                onClick={() => {
                                  if (selectedChannelOption) {
                                    handleMaterialChange(
                                      null,
                                      selectedChannelOption.value,
                                      "channel"
                                    );
                                  }
                                }}
                                disabled={!selectedChannelOption || actionLoading}
                              >
                                Add Drawer
                              </Button>
                              <Button
                                size="md"
                                outline
                                onClick={() => {
                                  setShowAddChannel(false);
                                  setEditingChannel(null);
                                  setSelectedChannelOption(null);
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        access.change_color && !isAdmin && (
                          <div
                            className="link pointer py-1"
                            onClick={() => {
                              setShowAddChannel(true);
                              loadAvailableOptions("channel");
                            }}
                            style={{ color: "#2C92E6" }}
                          >
                            <i className="fa-solid fa-plus me-1"></i> Add Drawer
                          </div>
                        )
                      )}
                    </div>
                  </>
                )}
                {selectedTab === "edges" && (
                  <>
                    <div className="p-3 overflow-scroll mb-8 fh">
                      <div className="d-flex flex-wrap column-gap-4 row-gap-3">
                        {edgeTapeMapping &&
                          edgeTapeMapping.map((mapping, index) => (
                            <>
                              <div className={"flex w-100"}>
                                <div
                                  id={"icon_" + index}
                                  className={`d-flex flex-column align-items-center`}
                                  onClick={() => {}}
                                  key={index}
                                >
                                  <ColorIcon className={`disabled `}>
                                    <img
                                      className="h-100 w-100"
                                      src={mapping.laminate.image}
                                    />
                                  </ColorIcon>
                                  <p className="m-0">
                                    {mapping.laminate.short_name}
                                  </p>
                                </div>
                                <UncontrolledTooltip
                                  target={"icon_" + index}
                                  placement="bottom"
                                >
                                  {mapping.laminate.name}
                                </UncontrolledTooltip>

                                <div className={"ms-4 flex-grow-1 me-4"}>
                                  <span
                                    className={"text-secondary fs-12px"}
                                  >{`${mapping.panel_thickness}mm Panel / ${mapping.thickness}mm Edge`}</span>

                                  <Select
                                    value={getEdgeMaterialOption(
                                      mapping.edge_material_identifier
                                    )}
                                    className="mt-1"
                                    styles={colourStyles}
                                    isDisabled={!access.manage_edges}
                                    options={edgeMaterials.map(
                                      (edgeMaterialOptionObj) => ({
                                        name: edgeMaterialOptionObj.name,
                                        label: edgeMaterialOptionObj.name,
                                        value: edgeMaterialOptionObj.identifier,
                                        thickness:
                                          edgeMaterialOptionObj.thickness,
                                        width: edgeMaterialOptionObj.width,
                                      })
                                    )}
                                    onChange={(selectedEdgeMaterial) => {
                                      handleEdgeMaterialChange({
                                        edgeCategory: mapping,
                                        materialIdentifier:
                                          selectedEdgeMaterial.value,
                                      });
                                    }}
                                    placeholder={"Select edge"}
                                  />
                                </div>
                              </div>
                            </>
                          ))}
                      </div>
                      {access.manage_edges && (
                        <div
                          className="position-fixed bottom-0 d-flex align-items-center px-2 py-3 border-top border-secondary-subtle"
                          style={{
                            background: "#dddddd",
                            right: 0,
                            width: "300px",
                          }}
                        >
                          <p className="mb-0 me-1">
                            Could not find the Edge Tape?
                          </p>
                          <UploadButton
                            onClick={() => setShowUploadEdgeTape(true)}
                          >
                            <i className="fa-solid fa-circle-plus me-1"></i>
                            Add
                          </UploadButton>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>

      <ConfirmationModal
        isOpen={showRemoveColorConfirmation}
        toggle={toggleRemoveColorConfirmation}
        onConfirm={() => deleteColorEntry(deleteOption)}
        message="Are you sure you want to remove the color?"
        headingText="Remove Color"
      />
    </>
  );
};

export default Colors;
